import { useState, useCallback, useLayoutEffect } from "react";

export default function useSmartModalLogic({
    
    // --- modal info
    subject: initialArgument = null,
    showModal: initialShowModal = false,

    modalMessage: initialModalMessage = null,

    // --- modal handlers
    onOpenHandler = async () => {},
    onCloseHandler = async () => {},
    onToggleHandler = async () => {},
    onConfirmHandler = async () => {},

    // --- button styles
    cancelButtonLabel: initialCancelButtonLabel = 'Cancel',
    cancelButtonColor: initialCancelButtonColor = 'secondary',

    confirmButtonLabel: initialConfirmButtonLabel = 'Confirm',
    confirmButtonColor: initialConfirmButtonColor = 'primary',

    // --- styles
    size = 'sm',
    messageStyle = {},
    textCenter = false,

    awaitConfirmCallback = true,

    // --- configurations
    hideModal = false,
    hideButtons = false,
    closeButton = false,
    staticDisplay = false,

    ...props

} = {}) {
    const [isLoading, setIsLoading] = useState(false)
    const [subject, setSubject] = useState(initialArgument)
    const [showModal, setShowModal] = useState(initialShowModal)
    const [modalMessage, setModalMessage] = useState(initialModalMessage)
    const [cancelButtonLabel, setCancelButtonLabel] = useState(initialCancelButtonLabel)
    const [cancelButtonColor, setCancelButtonColor] = useState(initialCancelButtonColor)
    const [confirmButtonLabel, setConfirmButtonLabel] = useState(initialConfirmButtonLabel)
    const [confirmButtonColor, setConfirmButtonColor] = useState(initialConfirmButtonColor)

    const [onCloseCallback, setOnCloseCallback] = useState(null)
    const [onConfirmCallback, setOnConfirmCallback] = useState(null)

    const closeModal = useCallback((data) => {
        setShowModal(() => {
            onCloseHandler(data)
            return false
        })

        setSubject(data)
        onToggleHandler(data)
        
    }, [
        setSubject, 
        setShowModal, 
        onCloseHandler,
        onToggleHandler,
    ])

    const toggleModalAndBind = useCallback((data) => {
        
        setShowModal(current => {
            const modalState = !current
            if(modalState === true) onOpenHandler(data)
            if(modalState === false) onCloseHandler(data)
            return modalState
        })

        setSubject(data)
        onToggleHandler(data)

    }, [
        setSubject, 
        setShowModal, 
        onOpenHandler,
        onCloseHandler,
        onToggleHandler,
    ])

    const toggleModal = useCallback(async(data) => {

        setShowModal(current => {
            const modalState = !current
            if(modalState === true) onOpenHandler(data)
            if(modalState === false) onCloseHandler(data)
            return modalState
        })

        setSubject(data)
        onToggleHandler(data)
        
    }, [
        setSubject, 
        setShowModal, 
        onOpenHandler,
        onCloseHandler,
        onToggleHandler,
    ])

    const handleCancel = useCallback(async () => {
        try{
            closeModal()
            setSubject(initialArgument)

            await onCloseHandler?.(subject)
            await onCloseCallback?.(subject)
        }catch(error){
            console.log(error)
            throw error
        }
    }, [
        subject, 
        closeModal, 
        onCloseHandler, 
        onCloseCallback,
        initialArgument,
    ]);

    const handleConfirm = useCallback(async (callback) => {
        try{
            setIsLoading(true)
            if(awaitConfirmCallback){
                await callback?.()
                await onConfirmHandler?.(subject)
                toggleModal()
            }else{
                toggleModal()
                callback?.()
                onConfirmHandler && await onConfirmHandler?.(subject)
            }
            onConfirmCallback && await onConfirmCallback?.(subject)
            setSubject(initialArgument)
        }catch(error){
            console.log(error)
            throw error
        }finally{
            setIsLoading(false)
        }
    }, [
        subject, 
        toggleModal, 
        setSubject,
        setIsLoading, 
        onConfirmHandler, 
        onConfirmCallback,
    ])

    useLayoutEffect(()=>{ setModalMessage?.(initialModalMessage) },[initialModalMessage])
    useLayoutEffect(()=>{ setCancelButtonLabel?.(initialCancelButtonLabel) },[initialCancelButtonLabel])
    useLayoutEffect(()=>{ setCancelButtonColor?.(initialCancelButtonColor) },[initialCancelButtonColor])
    useLayoutEffect(()=>{ setConfirmButtonLabel?.(initialConfirmButtonLabel) },[initialConfirmButtonLabel])
    useLayoutEffect(()=>{ setConfirmButtonColor?.(initialConfirmButtonColor) },[initialConfirmButtonColor])

    return {
        size,
        subject,
        isLoading,
        showModal,
        hideModal,
        closeButton,
        textCenter,
        hideButtons,
        messageStyle,
        closeModal,
        toggleModal,
        toggleModalAndBind,
        setOnCloseCallback,
        setOnConfirmCallback,
        onCancel: handleCancel,
        onConfirm: handleConfirm,
        modalMessage, setModalMessage,
        cancelButtonLabel, setCancelButtonLabel,
        cancelButtonColor, setCancelButtonColor,
        confirmButtonLabel, setConfirmButtonLabel,
        confirmButtonColor, setConfirmButtonColor,
        staticDisplay,
        
        ...props,
    };
}
